import { useState, useRef } from 'react'
import { useMembers } from '../../../hooks/useMembers'
import { useAuth } from '../../../hooks/useAuth'
import { compressImage } from '../../../lib/imageCompression'
import { supabase } from '../../../lib/supabase'
import { getSignedUrl } from '../../../lib/upload'
import { Spinner } from '../../../components/ui/Spinner'
import { Portal } from '../../../components/Portal'
import { cn } from '../../../lib/utils'

const RELATIONSHIPS = [
  'doctor','dentist','coach','teacher','therapist',
  'emergency','family','friend','other',
]

const uploadContactPhoto = async (file, householdId) => {
  const compressed = await compressImage(file, { maxWidthPx: 200, maxHeightPx: 200, quality: 0.85 })
  const path = `${householdId}/contacts/${crypto.randomUUID()}.jpg`
  const { error } = await supabase.storage
    .from('household-documents')
    .upload(path, compressed, { upsert: false, contentType: 'image/jpeg' })
  if (error) throw error
  return path
}

export const ContactForm = ({ contact, onSave, onClose }) => {
  const { household } = useAuth()
  const { data: members = [] } = useMembers()
  const fileRef = useRef(null)

  const [firstName,     setFirstName]     = useState(contact?.first_name      ?? '')
  const [lastName,      setLastName]      = useState(contact?.last_name        ?? '')
  const [relationship,  setRelationship]  = useState(contact?.relationship     ?? 'other')
  const [memberIds,     setMemberIds]     = useState(contact?.member_ids       ?? [])
  const [isEmergency,   setIsEmergency]   = useState(contact?.is_emergency     ?? false)
  const [organization,  setOrganization]  = useState(contact?.organization     ?? '')
  const [phonePrimary,  setPhonePrimary]  = useState(contact?.phone_primary    ?? '')
  const [phoneSecondary,setPhoneSecondary]= useState(contact?.phone_secondary  ?? '')
  const [email,         setEmail]         = useState(contact?.email            ?? '')
  const [address,       setAddress]       = useState(contact?.address          ?? '')
  const [notes,         setNotes]         = useState(contact?.notes            ?? '')
  const [photoFile,     setPhotoFile]     = useState(null)
  const [photoPreview,  setPhotoPreview]  = useState(null)
  const [saving,        setSaving]        = useState(false)
  const [error,         setError]         = useState('')

  const realMembers = members.filter((m) => !m.isShared)

  const toggleMember = (uid) => {
    setMemberIds((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    )
  }

  const handlePhoto = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPhotoFile(file)
    setPhotoPreview(URL.createObjectURL(file))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!firstName.trim()) { setError('First name is required.'); return }
    setSaving(true)
    setError('')
    try {
      let photo_path = contact?.photo_path ?? null
      if (photoFile) {
        photo_path = await uploadContactPhoto(photoFile, household.id)
      }
      await onSave({
        first_name:      firstName.trim(),
        last_name:       lastName.trim(),
        relationship,
        member_ids:      memberIds,
        is_emergency:    isEmergency,
        organization:    organization.trim() || null,
        phone_primary:   phonePrimary.trim() || null,
        phone_secondary: phoneSecondary.trim() || null,
        email:           email.trim() || null,
        address:         address.trim() || null,
        notes:           notes.trim() || null,
        photo_path,
      })
      onClose()
    } catch (err) {
      setError(err.message ?? 'Save failed.')
      setSaving(false)
    }
  }

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <form
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border flex-shrink-0">
          <h2 className="text-base font-bold text-text-primary">
            {contact ? 'Edit Contact' : 'New Contact'}
          </h2>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-4">
          {/* Photo */}
          <div className="flex items-center gap-4">
            <div
              onClick={() => fileRef.current?.click()}
              className="w-16 h-16 rounded-full bg-border/40 flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
            >
              {photoPreview ? (
                <img src={photoPreview} alt="" className="w-full h-full object-cover" />
              ) : (
                <svg className="w-7 h-7 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
            <button type="button" onClick={() => fileRef.current?.click()}
              className="text-sm text-primary font-medium">
              {photoPreview ? 'Change photo' : 'Add photo'}
            </button>
          </div>

          {/* Name */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div>
              <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">First Name *</label>
              <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jane"
                className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Last Name</label>
              <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
                placeholder="Smith"
                className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>

          {/* Relationship + Emergency */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div>
              <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Relationship</label>
              <select value={relationship} onChange={(e) => setRelationship(e.target.value)}
                className="w-full rounded-xl border border-border px-3 py-2.5 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30 capitalize">
                {RELATIONSHIPS.map((r) => (
                  <option key={r} value={r} className="capitalize">{r}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col justify-end pb-0.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <div
                  onClick={() => setIsEmergency((v) => !v)}
                  className={cn(
                    'relative w-10 h-5.5 rounded-full transition-colors',
                    isEmergency ? 'bg-rose-500' : 'bg-border',
                  )}
                >
                  <span className={cn(
                    'absolute top-0.5 w-4.5 h-4.5 bg-white rounded-full shadow transition-all',
                    isEmergency ? 'left-5' : 'left-0.5',
                  )} />
                </div>
                <span className="text-sm font-medium text-text-primary">Emergency</span>
              </label>
            </div>
          </div>

          {/* Organization */}
          <div>
            <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Organization</label>
            <input type="text" value={organization} onChange={(e) => setOrganization(e.target.value)}
              placeholder="Clinic or school name"
              className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>

          {/* Member association */}
          {realMembers.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Associated Members</label>
              <div className="flex flex-wrap gap-2">
                {realMembers.map((m) => {
                  const uid     = m.user_id
                  const name    = m.profiles?.display_name ?? 'Member'
                  const selected = memberIds.includes(uid)
                  return (
                    <button
                      key={uid}
                      type="button"
                      onClick={() => toggleMember(uid)}
                      className={cn(
                        'text-xs font-medium px-3 py-1.5 rounded-full border transition-colors',
                        selected
                          ? 'bg-primary text-white border-primary'
                          : 'bg-background text-text-secondary border-border hover:border-primary/50'
                      )}
                    >
                      {name}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Phones */}
          <div>
            <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Primary Phone</label>
            <input type="tel" value={phonePrimary} onChange={(e) => setPhonePrimary(e.target.value)}
              placeholder="+1 (555) 000-0000"
              className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Secondary Phone</label>
            <input type="tel" value={phoneSecondary} onChange={(e) => setPhoneSecondary(e.target.value)}
              placeholder="Optional"
              className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>

          {/* Address */}
          <div>
            <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Address</label>
            <input type="text" value={address} onChange={(e) => setAddress(e.target.value)}
              placeholder="123 Main St, City, State"
              className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={3} placeholder="Any additional notes…"
              className="w-full rounded-xl border border-border px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>

        <div className="px-5 py-4 border-t border-border flex gap-2 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 border border-border rounded-xl py-3 text-sm font-medium text-text-secondary">
            Cancel
          </button>
          <button type="submit" disabled={saving}
            className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <><Spinner size="sm" /> Saving…</> : 'Save Contact'}
          </button>
        </div>
      </form>
    </div>
    </Portal>
  )
}
